import React, {useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import classNames from "classnames";
import PlusButton from "@/pages/datalab/components/PlusButton";
import FilterPanelDropdown from "@/pages/datalab/components/UserFilter/FilterPanelDropdown";
import {useIntl} from "umi";
import {useFilterPanalData} from "@/pages/datalab/components/UserFilter/FilterPanel/hooks";
import {ChangeTabLayoutsType} from "@dbl/core/dashboard";
import SvgIcon from "@home/components/SvgIcon";
import {notification, Bubble} from "tea-component";
import './index.scss';
import FilterVsButton from "@/pages/datalab/components/UserFilter/FilterVsButton";
import {
  DEFAULT_NAME,
  DEFAULT_NAME_CN,
  DEFAULT_NAME_EN
} from "@/pages/datalab/components/UserFilter/FilterPanel/constant";
import {getLocalText} from "@/pages/datalab/utils/utils";
import {LANGUAGE_EN} from "@home/_config/config";
import CustomHoverBubble from "@/pages/datalab/components/CustomHoverBubble";
import {ScopeChartList, UserFilterControlFilterText} from "@/pages/datalab/components/BubbleListContent/AllBubbleList";
import type {
  IUserGroup,
  IFieldOption,
  IFilterValuesItemResponse,
  IUserFilterItem,
} from "@/pages/datalab/components/UserFilter/FilterPanel/type";
import type {IUserFilterConfig, TChangeTabLayoutsHandler} from "@dbl/core/dashboard";

interface IControlButtonProps {
  title: string;
  onDelete?: () => void;
  nameRef: React.RefObject<HTMLDivElement>;
}

interface IControlItemProps {
  control: IUserGroup;
  fieldOption: IFieldOption[];
  fieldValueMap: Record<string, IFilterValuesItemResponse[]>;
  stringLoading: boolean;
  isEdit: boolean;
  isEn: boolean;
  onConfirm?: (value: IUserGroup) => void;
  onDelete?: () => void;
  getStringData: (filterId: string) => void;
  scopeContent: React.ReactNode;
  onOpen?: () => void;
  header?: string;
}

interface IUserFilterGroupProps {
  userFilter: IUserFilterConfig;
  gameCode: string;
  changeTabLayoutsReduce: TChangeTabLayoutsHandler;
  isEdit: boolean;
}

export interface IUserFilterGroupRef {
  getUserFilter: () => IUserFilterConfig;
  setUserFilter: (value: IUserFilterConfig) => void;
}

const DashboardViewControlButton: React.FC<IControlButtonProps> = ({title, onDelete, nameRef}) => (
  <>
    {/* 需要 title属性显示 name不全时的全部*/}
    <div ref={nameRef} className={'dbl-user-control-name'}>{title}</div>
    {onDelete ? <SvgIcon onClick={onDelete} type="clonse" className="dbl-view-user-control-button__delete"/> : ''}
  </>
);

const DashboardEditControlButton: React.FC<IControlButtonProps> = ({title, onDelete, nameRef}) => (
  <>
    {/* 需要 title属性显示 name不全时的全部*/}
    <div ref={nameRef} className={'dbl-user-control-name'}>{title}</div>
    <div className={'dbl-edit-user-control-buttons'}>
      <SvgIcon type="pencil-m" className=""/>
      {onDelete ? (<SvgIcon onClick={onDelete} type="clonse" className=""/>) : ''}
    </div>
  </>
);

const ControlItem: React.FC<IControlItemProps> = ({
  control,
  fieldOption,
  fieldValueMap,
  stringLoading,
  isEdit,
  isEn,
  onConfirm,
  onDelete,
  getStringData,
  scopeContent,
  onOpen,
  header,
}) => {
  const { name, name_en, name_cn, summaryCn, summaryEn, is_all_users } = control;
  const title = getLocalText(isEn, name_cn, name_en, name);
  const [openStatus, setOpenStatus] = useState(false);
  const nameRef = useRef<HTMLDivElement | null>(null);
  const DropButton = isEdit ? DashboardEditControlButton : DashboardViewControlButton;
  
  return (<CustomHoverBubble
    content={(<>
      <UserFilterControlFilterText title={title} text={is_all_users ? '' : (isEn ? summaryEn : summaryCn)}/>
      {scopeContent}
    </>)}
    overlayClassName={'dbl-dashboard-filter-info-bubble dbl-show-filter-overlay'}
    referenceElement={nameRef.current}
    visible={!openStatus}
    placement={"top"}
    placementOffset={[0, 16]}
    flipped={true}
  >
    <FilterPanelDropdown
      className={classNames('dbl-dashboard-user-control', {'dbl-dashboard-user-control-edit': isEdit})}
      value={control}
      fieldOption={fieldOption}
      stringDataMap={fieldValueMap}
      stringLoading={stringLoading}
      max={5}
      defaultName={DEFAULT_NAME}
      defaultNameEn={DEFAULT_NAME_EN}
      defaultNameCn={DEFAULT_NAME_CN}
      header={header}
      button={<DropButton nameRef={nameRef} title={title} onDelete={onDelete}/>}
      onConfirm={(value) => {
        setOpenStatus(false);
        onConfirm?.(value);
      }}
      onCancel={() => {
        setOpenStatus(false);
      }}
      getStringData={getStringData}
      dropdownProps={{
        appearance: 'pure',
      }}
      onOpenClose={(open: boolean) => {
        if (open) onOpen?.();
        setOpenStatus(open);
      }}
    />
  </CustomHoverBubble>);
};

const UserFilterGroup: React.ForwardRefRenderFunction<IUserFilterGroupRef, IUserFilterGroupProps> = (props, ref) => {
  const { userFilter, gameCode, changeTabLayoutsReduce, isEdit } = props;
  
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const normalizedUserFilter = useMemo<IUserFilterConfig>(() => userFilter ?? { controls: [] }, [userFilter]);
  const [ value, setValue ] = useState<IUserFilterConfig>(normalizedUserFilter);
  
  useImperativeHandle(ref, () => ({
    getUserFilter: () => value,
    setUserFilter: setValue,
  }));
  
  const {
    fieldOption,
    stringDataMap: fieldValueMap,
    stringLoading,
    getFieldData: getFields,
    getStringData: getFieldValue,
  } = useFilterPanalData({ gameCode });
  
  useEffect(() => {
    setValue(normalizedUserFilter);
  }, [normalizedUserFilter]);
  
  useEffect(() => {
    getFields(gameCode);
  }, [gameCode]);
  
  const controls = value?.controls ?? [];
  const showDeleteBtn = controls.length > 1;
  
  const handleConfirm = (newControl?: IUserGroup) => {
    if (!newControl) return;
    const hasAll = controls.some(item => !item?.filters?.[0]?.value && item.id !== newControl.id);
    if (hasAll && !newControl?.filters?.[0]?.value) {
      notification.warning({
        description: formatMessage({ id: 'dbl_filter_all_users_add' }),
      });
      return;
    }
    // !newControl.filters?.[0]?.value 是false时, is_all_users设置为undefined
    newControl.is_all_users = !newControl.filters?.[0]?.value || undefined;
    
    const updateIndex = controls.findIndex(item => item.id === newControl.id);
    
    setValue((prev) => {
      const nextControls = [...(prev.controls ?? [])];
      if (updateIndex === -1) {
        nextControls.push(newControl);
      } else {
        nextControls[updateIndex] = newControl;
      }
      return {...prev, controls: nextControls};
    });
    // 报表编辑页面更新 需要保存的User Filter
    changeTabLayoutsReduce?.(updateIndex === -1 ? ChangeTabLayoutsType.ADD_USER_FILTER_CONTROL : ChangeTabLayoutsType.UPDATE_USER_FILTER_CONTROL, { newControl });
  };
  
  const handleDelete = (control: IUserGroup) => {
    changeTabLayoutsReduce?.(ChangeTabLayoutsType.DELETE_USER_FILTER_CONTROL, {deleteId: control?.id});
    setValue((prev) => ({
      ...prev,
      controls: prev?.controls?.filter(item => item.id !== control?.id) || [],
    }));
  };
  
  const handleGetFieldValue = (filterId?: string) => {
    // 无gameCode和id或者缓存中已有数据，不请求接口
    if (!gameCode || !filterId || fieldValueMap?.[filterId]) return;
    getFieldValue(gameCode, filterId);
  };
  
  const scopeContent = useMemo(() => {
    if (!value?.show_scope) return '';
    if (Array.isArray(value?.scope_charts)) {
      return (<ScopeChartList charts={value?.scope_charts || []}/>);
    }
    return '';
  }, [value?.show_scope, value?.scope_charts]);
  
  return (<div className={'dbl-dashboard-user-filter'}>
    {
      controls.map((control, index) => {
        return (
          <React.Fragment key={control?.id}>
            <ControlItem
              getStringData={handleGetFieldValue}
              onConfirm={handleConfirm}
              onDelete={showDeleteBtn ? () => {handleDelete(control)} : undefined}
              fieldOption={fieldOption}
              fieldValueMap={fieldValueMap}
              stringLoading={stringLoading}
              scopeContent={scopeContent}
              header={formatMessage({ id: 'dbl_filter_user_filter_setting' })}
              control={control}
              isEn={isEn}
              isEdit={isEdit}
              onOpen={() => {
                const controlFilters = control?.filters ?? [];
                const cache: { [key: string]: boolean; } = {};
                (controlFilters as IUserFilterItem[]).forEach((filterItem) => {
                  const { filter_id: filterId, fieldType } = filterItem || {};
                  const hasCache = filterId ? fieldValueMap?.[filterId] || cache[filterId] : false;
                  if (fieldType === 'string' && filterId && !hasCache) {
                    cache[filterId] = true;
                    getFieldValue(gameCode, filterId);
                  }
                });
              }}
            />
            {index < controls.length - 1 && <FilterVsButton />}
          </React.Fragment>
        );
      })
    }
    
    <FilterPanelDropdown
      className={'dbl-dashboard-user-filter-add'}
      fieldOption={fieldOption}
      stringDataMap={fieldValueMap}
      max={5}
      defaultName={DEFAULT_NAME}
      defaultNameEn={DEFAULT_NAME_EN}
      defaultNameCn={DEFAULT_NAME_CN}
      header={formatMessage({ id: 'dbl_filter_user_filter_setting' })}
      button={
        <Bubble content={controls?.length >= 4 ? formatMessage({ id: 'dbl_filter_add_control_group_disabled' }) : undefined}>
          <div>
            <PlusButton
              className='dbl-dashboard-filter-wrap-add-filter'
              type={'text'}
              title={formatMessage({ id: 'dbl_filter_add_control_group' })}
              disabled={controls.length >= 4}
            />
          </div>
        </Bubble>
      }
      dropdownProps={{
        appearance: 'pure'
      }}
      onConfirm={handleConfirm}
      // onCancel={() => {
      //   // 取消时恢复原值
      // }}
      getStringData={handleGetFieldValue}
    />
    
  </div>);
  
};

export default React.forwardRef<IUserFilterGroupRef, IUserFilterGroupProps>(UserFilterGroup);